// HashtagPromptWizard.jsx — auto-advance + Answer Tray + click-to-edit
import React, { useState, useEffect } from 'react';

const QUESTIONS = [
  {
    key: 'goal',
    title: "What’s the primary goal?",
    short: 'Goal',
    options: [
      { value: 'reach', label: 'Grow reach', icon: '📈' },
      { value: 'saves', label: 'Drive saves', icon: '💾' },
      { value: 'affiliate', label: 'Clicks/Affiliate', icon: '🛒' },
      { value: 'engagement', label: 'Community', icon: '💬' },
      { value: 'authority', label: 'Brand cred', icon: '🧩' }
    ]
  },
  {
    key: 'content_type',
    title: 'What type of content?',
    short: 'Type',
    options: [
      { value: 'photo', label: 'Photo', icon: '📷' },
      { value: 'carousel', label: 'Carousel', icon: '🗂️' },
      { value: 'reel', label: 'Reel', icon: '🎞️' },
      { value: 'story', label: 'Story', icon: '🫧' },
      { value: 'live', label: 'Live/Event', icon: '🎤' }
    ]
  },
  {
    key: 'niche',
    title: 'Pick your niche',
    short: 'Niche',
    options: [
      { value: 'fashion', label: 'Fashion', icon: '👗' },
      { value: 'beauty', label: 'Beauty', icon: '💄' },
      { value: 'wellness', label: 'Wellness', icon: '🧘‍♀️' },
      { value: 'career_legal', label: 'Career/Legal', icon: '⚖️' },
      { value: 'travel', label: 'Travel', icon: '✈️' },
      { value: 'food', label: 'Food', icon: '🍝' },
      { value: 'tech_tools', label: 'Tech/Tools', icon: '🧠' },
      { value: 'lifestyle', label: 'Lifestyle', icon: '🧺' },
      { value: 'other', label: 'Other', icon: '➕', input: true, inputPlaceholder: 'Type your niche' }
    ]
  },
  {
    key: 'audience_region',
    title: 'Who’s this for?',
    short: 'Audience',
    options: [
      { value: 'global', label: 'Global', icon: '🌍' },
      { value: 'us_ca', label: 'US/Canada', icon: '🇺🇸' },
      { value: 'eu', label: 'Europe', icon: '🇪🇺' },
      { value: 'local', label: 'Local/City', icon: '📍', input: true, inputPlaceholder: 'City/Area' }
    ]
  },
  {
    key: 'tone',
    title: 'Pick a vibe',
    short: 'Tone',
    options: [
      { value: 'inspirational', label: 'Inspirational', icon: '✨' },
      { value: 'educational', label: 'Educational', icon: '📚' },
      { value: 'bold', label: 'Bold/Edgy', icon: '⚡' },
      { value: 'minimal', label: 'Minimal/Clean', icon: '🫥' },
      { value: 'playful', label: 'Playful/Fun', icon: '🎈' }
    ]
  },
  {
    key: 'seasonality',
    title: 'Any seasonal or trending angle?',
    short: 'Timing',
    options: [
      { value: 'evergreen', label: 'None/Evergreen', icon: '🌿' },
      { value: 'seasonal', label: 'Seasonal', icon: '🍂' },
      { value: 'holiday', label: 'Holiday', icon: '🎁' },
      { value: 'trending_audio', label: 'Trending audio', icon: '🎵', input: true, inputPlaceholder: 'Audio name' },
      { value: 'event', label: 'Event-based', icon: '🎉' }
    ]
  },
  {
    key: 'cta',
    title: 'Soft call-to-action?',
    short: 'CTA',
    options: [
      { value: 'like_comment', label: 'Like/Comment', icon: '❤️' },
      { value: 'save_share', label: 'Save/Share', icon: '🔖' },
      { value: 'click_bio', label: 'Click bio', icon: '🔗' },
      { value: 'dm_me', label: 'DM me', icon: '📥' },
      { value: 'shop_now', label: 'Shop now', icon: '🛍️' }
    ]
  }
];

export default function HashtagPromptWizard({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cl_wizard_answers') || '{}'); }
    catch { return {}; }
  });
  const [showInput, setShowInput] = useState(false);
  const [tempInput, setTempInput] = useState('');

  const current = QUESTIONS[index];
  const isLast = index === QUESTIONS.length - 1;

  useEffect(() => {
    localStorage.setItem('cl_wizard_answers', JSON.stringify(answers));
  }, [answers]);

  const labelForTray = (q, val) => {
    if (!val) return '•';
    if (typeof val === 'object') {
      if (val.inputText) return val.inputText;
      const o = q.options.find(x => x.value === val.value);
      return o?.label || val.value;
    }
    const o = q.options.find(x => x.value === val);
    return o?.label || String(val);
  };

  const handleSelect = (opt) => {
    if (opt.input) {
      setShowInput(true);
      setTempInput('');
      setAnswers(prev => ({ ...prev, [current.key]: { value: opt.value, inputText: '' } }));
    } else {
      commitAnswer(opt.value);
    }
  };

  const commitAnswer = (value, inputText = null, nextBehavior = 'forward') => {
    const answer = inputText ? { value, inputText } : value;
    const nextAnswers = { ...answers, [current.key]: answer };
    setAnswers(nextAnswers);
    if (showInput) setShowInput(false);

    if (isLast && nextBehavior === 'forward') {
      onComplete?.(normalize(nextAnswers));
      return;
    }

    if (nextBehavior === 'forward') {
      const target = findNextUnanswered(nextAnswers, Math.min(index + 1, QUESTIONS.length - 1));
      setTimeout(() => setIndex(target), 160);
    } else if (typeof nextBehavior === 'number') {
      setTimeout(() => setIndex(nextBehavior), 100);
    }
  };

  const handleInputSubmit = () => {
    const val = tempInput.trim();
    if (val) commitAnswer(answers[current.key].value, val, 'forward');
  };

  const requiresInput = (q, ans) => {
    const inputOpt = q.options.find(o => o.input);
    return ans?.value === inputOpt?.value && !ans?.inputText;
  };

  const findNextUnanswered = (a, start) => {
    for (let i = start; i < QUESTIONS.length; i++) {
      const k = QUESTIONS[i].key;
      if (!a[k] || (typeof a[k] === 'object' && requiresInput(QUESTIONS[i], a[k]))) {
        return i;
      }
    }
    onComplete?.(normalize(a));
    return start;
  };

  const jumpTo = (i) => {
    setShowInput(false);
    setTempInput('');
    setIndex(i);
  };

  const isSelected = (opt) => {
    const ans = answers[current.key];
    if (!ans) return false;
    return typeof ans === 'string' ? ans === opt.value : ans.value === opt.value;
  };

  return (
    <div className="wizard-shell">
      {/* Answer Tray */}
      <div className="wizard-tray">
        {QUESTIONS.map((q, i) => {
          const active = i === index;
          const ans = answers[q.key];
          return (
            <button
              key={q.key}
              className={`tray-chip ${active ? 'active' : ''} ${ans ? 'filled' : ''}`}
              onClick={() => jumpTo(i)}
              title={`${q.short}: ${labelForTray(q, ans)}`}
              aria-current={active ? 'step' : undefined}
            >
              <span className="tray-chip-title">{q.short}</span>
              <span className="tray-chip-value">{labelForTray(q, ans)}</span>
            </button>
          );
        })}
      </div>

      {/* Step Card */}
      <div className="wizard-card chic-swipe-in" key={current.key}>
        <div className="wizard-stepbar">
          <div className="wizard-progress" style={{ width: `${((index+1)/QUESTIONS.length)*100}%` }} />
        </div>
        <h3 className="wizard-title">{current.title}</h3>

        {!showInput && (
          <div className="wizard-options-row" role="list">
          {current.options.map((opt) => (
            <button
              key={opt.value}
              className={`option-pill ${isSelected(opt) ? 'selected' : ''}`}
              onClick={() => handleSelect(opt)}
              role="listitem"
              aria-pressed={isSelected(opt)}
              title={opt.label}
            >
              <span className="option-icon">{opt.icon}</span>
              <span className="option-label">
                {opt.label}
                {opt.input && isSelected(opt) && !tempInput && (
                  <span className="option-inline-hint"> — type below</span>
                )}
              </span>
            </button>
          ))}
        </div>
        )}

        {showInput && (
          <div className="wizard-input-section">
            <input
              className="option-input"
              placeholder={current.options.find(o => o.input)?.inputPlaceholder || 'Type here'}
              value={tempInput}
              onChange={(e) => setTempInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInputSubmit()}
              autoFocus
            />
            <button className="wizard-continue-btn" onClick={handleInputSubmit}>Continue</button>
          </div>
        )}
      </div>
    </div>
  );
}

function normalize(a) {
  return {
    goal: unwrap(a.goal),
    content_type: unwrap(a.content_type),
    niche: a.niche?.value === 'other' ? (a.niche?.inputText || 'other') : unwrap(a.niche),
    audience_region: a.audience_region?.value === 'local' ? 'local' : unwrap(a.audience_region),
    audience_city: a.audience_region?.value === 'local' ? (a.audience_region?.inputText || null) : null,
    tone: unwrap(a.tone),
    seasonality: typeof a.seasonality === 'string' ? a.seasonality : a.seasonality?.value,
    trend_audio: a.seasonality?.value === 'trending_audio' ? (a.seasonality?.inputText || null) : null,
    cta: unwrap(a.cta),
  };
}
const unwrap = (v) => (typeof v === 'object' && v ? (v.inputText || v.value) : v);
